import { errorFrom } from "@/lib/errors";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Clapperboard, Download, Loader2, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/primitives/Button";
import { animateScene, api, clipVideoUrl, listClips, type AspectRatio, type Clip, type Scene } from "@/lib/api";

const VIDEO_COST = "0,42";
const POLL_MS = 5000;
const inProgress = (c?: Clip | null) => !!c && (c.status === "PENDING" || c.status === "GENERATING");

// Scene → active image → Kling (existing pipeline) → preview → download. Backend is the source of truth.
export function SceneVideoPanel({
  scene,
  index,
  aspect,
  disabled,
}: {
  scene: Scene;
  index: number;
  aspect: AspectRatio;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [clips, setClips] = useState<Clip[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const submitLock = useRef(false);

  const load = useCallback(async () => {
    try {
      const list = await listClips(scene.id);
      list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setClips(list);
    } catch {
      /* state is rebuilt on next poll */
    } finally {
      setLoaded(true);
    }
  }, [scene.id]);

  useEffect(() => {
    load();
  }, [load]);

  const latest = clips[0] ?? null;
  const ready = clips.filter((c) => c.status === "READY" && c.video_url);
  const current = ready.find((c) => c.id === selectedId) ?? ready[0] ?? null;

  // Poll while a generation is active (also after refresh / navigation back).
  useEffect(() => {
    if (!inProgress(latest)) return;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [latest?.id, latest?.status, load]);

  // Authenticated fetch of the finished MP4 -> object URL (versioned, cache-busted by clip id).
  useEffect(() => {
    if (!current) {
      setSrc(null);
      return;
    }
    let url: string | null = null;
    let cancelled = false;
    api
      .get(clipVideoUrl(current.id, current.created_at || current.id), { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        url = URL.createObjectURL(r.data);
        setSrc(url);
      })
      .catch(() => !cancelled && setSrc(null));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [current?.id]);

  const generate = async () => {
    if (submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    setConfirming(false);
    try {
      const clip = await animateScene(scene.id);
      if (clip.status === "FAILED") toast.error(t("sceneVideo.generateFailed"));
      else toast.success(`Video della scena ${index + 1} in creazione`);
      await load();
    } catch (e: any) {
      toast.error(errorFrom(e, "sceneVideo.startFailed"));
    } finally {
      setSubmitting(false);
      submitLock.current = false;
    }
  };

  const download = async () => {
    if (!current) return;
    setDownloading(true);
    try {
      const r = await api.get(clipVideoUrl(current.id), { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `scena-${index + 1}-v${ready.length - ready.indexOf(current)}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("sceneVideo.downloadFailed"));
    } finally {
      setDownloading(false);
    }
  };

  const hasImage = !!scene.image;
  const busy = disabled || submitting || inProgress(latest);
  const label = ready.length > 0 ? t("sceneVideo.newVersion") : latest?.status === "FAILED" || latest?.status === "STALLED" ? t("common.retry") : t("sceneVideo.generate");

  return (
    <div data-testid={`scene-video-panel-${index}`} className="mt-4 border-t border-[#2E7BFF]/15 pt-4">
      <span className="mb-2 block text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Video della scena</span>

      {current && (
        <div
          data-testid={`scene-video-frame-${index}`}
          className={cn(
            "relative mx-auto overflow-hidden rounded-lg border border-[#2E7BFF]/40 bg-black shadow-[0_0_24px_-10px_rgba(46,123,255,0.6)]",
            aspect === "9:16" ? "aspect-[9/16] w-32 sm:w-36" : "aspect-video w-full",
          )}
        >
          {src ? (
            <video data-testid={`scene-video-player-${index}`} src={src} controls playsInline preload="metadata" className="h-full w-full object-contain" />
          ) : (
            <div className="grid h-full w-full place-items-center"><Loader2 className="h-5 w-5 animate-spin text-[#2E7BFF]" /></div>
          )}
        </div>
      )}

      {loaded && (
        <p data-testid={`scene-video-status-${index}`} className="mt-3 text-xs">
          {inProgress(latest) ? (
            <span className="inline-flex items-center gap-2 text-[#7FD4FF]"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("sceneVideo.creating")}</span>
          ) : latest?.status === "FAILED" || latest?.status === "STALLED" ? (
            <span className="text-[#FF8A8A]">{t("sceneVideo.failed")}</span>
          ) : ready.length > 0 ? (
            <span className="text-[#3DDC84]">{t("sceneVideo.ready")}{ready.length > 1 ? ` · ${t("sceneVideo.versions", { count: ready.length })}` : ""}</span>
          ) : !hasImage ? (
            <span className="text-muted-foreground">{t("sceneVideo.needImage")}</span>
          ) : (
            <span className="text-muted-foreground">{t("sceneVideo.readyToGenerate")}</span>
          )}
        </p>
      )}

      {ready.length > 1 && (
        <select
          data-testid={`scene-video-version-${index}`}
          value={current?.id ?? ""}
          onChange={(e) => setSelectedId(e.target.value)}
          className="field-premium mt-2 h-8 !rounded-md px-2 text-xs"
        >
          {ready.map((c, i) => (
            <option key={c.id} value={c.id}>{t("sceneImage.version", { n: ready.length - i })}{i === 0 ? ` (${t("sceneVideo.latest")})` : ""}</option>
          ))}
        </select>
      )}

      <div className="mt-3 flex flex-col gap-2">
        <Button
          type="button"
          size="sm"
          data-testid={`scene-video-generate-btn-${index}`}
          disabled={busy || !hasImage}
          title={!hasImage ? t("sceneVideo.needImage") : undefined}
          onClick={() => setConfirming(true)}
          className="w-full"
        >
          {submitting || inProgress(latest) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : ready.length > 0 ? <RefreshCw className="h-3.5 w-3.5" /> : <Clapperboard className="h-3.5 w-3.5" />}
          <span className="ml-1.5">{label}</span>
        </Button>
        {current && (
          <Button type="button" variant="secondary" size="sm" data-testid={`scene-video-download-btn-${index}`} disabled={downloading} onClick={download} className="w-full">
            {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            <span className="ml-1.5">{t("common.downloadMp4")}</span>
          </Button>
        )}
      </div>

      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setConfirming(false)}>
          <div className="glass-panel w-full max-w-md rounded-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid={`scene-video-confirm-${index}`}>
            <h3 className="font-display text-base font-semibold uppercase tracking-wide text-foreground">
              {ready.length > 0 ? t("sceneVideo.newVersion") : t("sceneVideo.generate")} · {t("sceneImage.sceneN", { n: index + 1 })}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("sceneVideo.confirmBody")} <span className="text-[#7FD4FF]">{aspect}</span>.
              {t("common.predictedCost")}: <strong className="text-[#7FD4FF]">${VIDEO_COST}</strong>. {t("common.startsAfterConfirm")}
              {ready.length > 0 && ` ${t("sceneVideo.keepPrevious")}`}
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setConfirming(false)} data-testid={`scene-video-confirm-cancel-${index}`}>{t("common.cancel")}</Button>
              <Button size="sm" onClick={generate} data-testid={`scene-video-confirm-accept-${index}`}>{t("common.confirmGenerate")}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
